/**
 * Tsuvi 品牌组件。
 *
 * - <LogoMark size/>     纯图形标（用于 favicon 邻近场景 / 需要纯 mark 的地方）
 * - <Logo variant/>      图形标 + 字标横排；variant="nav"(导航栏) | "workbench"(工作台)
 *
 * 图形标为三层堆叠的层片，暗喻 3D 打印逐层堆叠（增材制造）。
 * 字标 "Tsuvi" 用品牌渐变填充（见 --grad-brand）。
 *
 * 注：颜色使用 currentColor / 渐变，自适应深色主题。
 */

import type { CSSProperties } from "react";
import { LocalizedLink } from "../lib/lang";
import { useSiteConfig } from "../hooks/useSiteConfig";

/** 品牌渐变唯一 id（多实例同页需唯一，否则 SVG 渐变引用会冲突） */
let markIdSeq = 0;

/** 纯图形标：三层（+顶点）堆叠，渐变填充。 */
export function LogoMark({
  size = 28,
  className,
  title,
}: {
  size?: number;
  className?: string;
  title?: string;
}) {
  // 每实例唯一渐变 id，避免同页多引用冲突
  const gid = `tsuvi-mark-${++markIdSeq}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      className={className}
      role={title ? "img" : "presentation"}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      <title>{title}</title>
      <defs>
        <linearGradient id={gid} x1="0" y1="48" x2="0" y2="3" gradientUnits="userSpaceOnUse">
          <stop stopColor="#fb7185" />
          <stop offset="0.55" stopColor="#ff8a3d" />
          <stop offset="1" stopColor="#ffd7b5" />
        </linearGradient>
      </defs>
      <g fill={`url(#${gid})`}>
        <rect x="4" y="31" width="40" height="6.5" rx="3" />
        <rect x="9" y="21" width="30" height="6.5" rx="3" />
        <rect x="14" y="11" width="20" height="6.5" rx="3" />
        <rect x="20" y="3" width="8" height="5.5" rx="2.5" />
      </g>
    </svg>
  );
}

/** 字标，品牌渐变填充。brandName 从站点配置读，兜底 "Tsuvi"。 */
function Wordmark({ style }: { style?: CSSProperties }) {
  const { config } = useSiteConfig();
  const name = config?.brandName ?? "Tsuvi";
  return (
    <span className="tsuvi-wordmark" style={style}>
      {name}
    </span>
  );
}

/**
 * 完整 Logo：图形标 + 字标，作为可点击的首页链接。
 * variant="nav" → 导航栏（落地页/定价/法律页）；variant="workbench" → 工作台顶栏。
 */
export default function Logo({
  variant = "nav",
}: {
  variant?: "nav" | "workbench";
}) {
  const markSize = variant === "workbench" ? 22 : 26;
  return (
    <LocalizedLink
      to="/"
      className={variant === "workbench" ? "wb-logo tsuvi-logo" : "logo tsuvi-logo"}
      aria-label="Tsuvi"
    >
      <LogoMark size={markSize} />
      <Wordmark />
    </LocalizedLink>
  );
}
